import { useSyncExternalStore } from 'react';
import { createAreaCardStats } from '../analytics/areaCards.js';
import { MAX_COMPARED } from './areaShare.js';

// The areas being compared (design A "Kolumner" of the 2026-11-01 "Jämföra områden" canvas, the owner's pick): a
// double click on an area, in either view, adds it to one comparison card (CompareCard.js) or takes it out again,
// instead of opening a card of its own. One list for both views, kept for the tab's session (sessionStorage), so
// it follows a switch of view and a reload; at most MAX_COMPARED, the most a shared link (/jamfor/…) carries.
//
// The area under the pointer (`hover`) is the card's dashed "Pekar på" column, fed by the views' area hover.

const STORAGE_KEY = 'compare_areas_v1';

const readStorage = () => {
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(parsed)
            ? parsed.filter((area) => area && typeof area.code === 'string' && area.code).slice(0, MAX_COMPARED)
            : [];
    } catch (error) {
        return [];
    }
};

let areas = null;
let hover = null;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getComparedAreas = () => {
    if (areas == null) areas = typeof window === 'undefined' ? [] : readStorage();
    return areas;
};
const getHover = () => hover;

const setAreas = (next) => {
    areas = next;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next.map(({ code, name }) => ({ code, name }))));
    } catch (error) {
        // Private windows may refuse storage; the comparison then lasts this page load.
    }
    emit();
};

// Opens and closes count like the sticky area cards did (map.area_popup / map.area_compare, analytics/areaCards.js),
// so the admin's "Area cards" charts keep their meaning: an area added to or taken out of the comparison.
const stats = createAreaCardStats('kartvy');

export const isComparedArea = (code) => getComparedAreas().some((area) => area.code === code);

// Adds the area, or takes it out when it's already there. Returns 'added', 'removed' or 'full'.
export const toggleComparedArea = (code, name, { view = 'kartvy' } = {}) => {
    if (!code) return null;
    const current = getComparedAreas();
    if (current.some((area) => area.code === code)) {
        setAreas(current.filter((area) => area.code !== code));
        stats.closed(code, 'double_click', view);
        return 'removed';
    }
    if (current.length >= MAX_COMPARED) return 'full';
    setAreas([...current, { code, name: name || code }]);
    stats.opened(code, view);
    return 'added';
};

// how: for the analytics (close_button: its ✕ in the card, map_number: its number on the map).
export const removeComparedArea = (code, { view = 'kartvy', how = 'close_button' } = {}) => {
    const current = getComparedAreas();
    if (!current.some((area) => area.code === code)) return;
    setAreas(current.filter((area) => area.code !== code));
    stats.closed(code, how, view);
};

export const clearComparedAreas = ({ view = 'kartvy' } = {}) => {
    const current = getComparedAreas();
    if (!current.length) return;
    setAreas([]);
    current.forEach((area) => stats.closed(area.code, 'close_all', view));
};

// Puts the areas in the order of `codes` (a column dragged to another place, useColumnDrag.js); any not in it keep
// their order after them. The numbers on the map follow.
export const reorderComparedAreas = (codes) => {
    const current = getComparedAreas();
    const rank = new Map(codes.map((code, index) => [code, index]));
    const next = current
        .map((area, index) => ({ area, rank: rank.has(area.code) ? rank.get(area.code) : codes.length + index }))
        .sort((a, b) => a.rank - b.rank)
        .map(({ area }) => area);
    if (next.every((area, index) => area === current[index])) return;
    setAreas(next);
};

// The area under the pointer; a view clears it when the pointer leaves the map.
export const setCompareHover = (code, name) => {
    const next = code ? { code, name: name || code } : null;
    if (hover?.code === next?.code) return;
    hover = next;
    emit();
};

export const useComparedAreas = () => useSyncExternalStore(subscribe, getComparedAreas, () => []);

// The comparison's homes shown in listvy (its page's "Visa bostäder", ComparePage.js): listvy's "Jämför N" chip on
// phones then stands aside and only the homes show (the owner, 2026-11-03). Noted as the comparison it was, so an
// area added or taken out brings the chip back; the comparison itself stays (kartvy's card, the desktop panel).
const HOMES_SHOWN_KEY = 'compare_homes_shown_v1';
const comparisonKey = (list) => list.map((area) => area.code).sort().join(',');
let homesShown = null;
const getHomesShown = () => {
    if (homesShown == null) {
        try {
            homesShown = window.sessionStorage.getItem(HOMES_SHOWN_KEY) || '';
        } catch (error) {
            homesShown = '';
        }
    }
    return homesShown;
};

export const noteComparedHomesShown = () => {
    homesShown = comparisonKey(getComparedAreas());
    try {
        window.sessionStorage.setItem(HOMES_SHOWN_KEY, homesShown);
    } catch (error) {
        // Without storage it holds for this page load.
    }
    emit();
};

// True while the comparison is the one whose homes were shown.
export const useComparedHomesShown = () => {
    const list = useComparedAreas();
    const shown = useSyncExternalStore(subscribe, getHomesShown, () => '');
    return list.length > 0 && shown === comparisonKey(list);
};
export const useCompareHover = () => useSyncExternalStore(subscribe, getHover, () => null);
